import isNil from "lodash/isNil";

import { GroupRegistrationText } from "@/constants/Schedule";
import { ModalTestID } from "@/constants/TestID";
import { IllustrationSize } from "@/constants/UI";
import GroupClassSessionHosts from "@/ui/components/groupClasses/series/session/GroupClassSessionHosts";
import GroupSectionDetailRow from "@/ui/components/groupClasses/series/session/GroupSectionDetailRow";
import BottomSheetModal from "@/ui/composed/modal/BottomSheetModal";
import type { AvatarCardProps } from "@/ui/core";
import { Button, FAIconName, Text } from "@/ui/core";
import { BaseCard, HStack, VStack } from "@/ui/layout";
import { getIllustrationIcon } from "@/ui/theme/icons/illustrations/groupClasses";
import { joinTestIDs, pluralize } from "@/utils/string.util";

interface ConfirmGroupRegistrationBottomSheetProps {
  hosts?: AvatarCardProps[];
  iconName?: string;
  isOpen: boolean;
  isRegistering?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onDismissAnimationEnd?: () => void;
  recurrenceString?: string;
  sessionCount?: number;
  timeRange?: string;
  title: string;
}

const baseTestID = ModalTestID.ConfirmGroupRegistration;

type SummaryCardProps = Pick<
  ConfirmGroupRegistrationBottomSheetProps,
  | "hosts"
  | "iconName"
  | "recurrenceString"
  | "sessionCount"
  | "timeRange"
  | "title"
>;

const SummaryCard = ({
  hosts,
  iconName,
  recurrenceString,
  sessionCount,
  timeRange,
  title,
}: SummaryCardProps) => (
  <BaseCard isDisabled testID={joinTestIDs(baseTestID, "card")}>
    <VStack gap={16}>
      <HStack alignItems="center" gap={12}>
        {iconName &&
          getIllustrationIcon({
            height: IllustrationSize.Card,
            iconName,
            width: IllustrationSize.Card,
          })}
        <Text
          flexShrink={1}
          testID={joinTestIDs(baseTestID, "title")}
          variant="bodyMedium"
        >
          {title}
        </Text>
      </HStack>
      <VStack gap={8}>
        {recurrenceString && (
          <GroupSectionDetailRow
            icon={FAIconName.Refresh}
            testID={joinTestIDs(baseTestID, "recurrence")}
            text={recurrenceString}
          />
        )}
        {timeRange && (
          <GroupSectionDetailRow
            icon={FAIconName.Clock}
            testID={joinTestIDs(baseTestID, "time-range")}
            text={timeRange}
          />
        )}
        {!isNil(sessionCount) && sessionCount > 0 && (
          <GroupSectionDetailRow
            icon={FAIconName.ArrowsLeftRight}
            testID={joinTestIDs(baseTestID, "sessions")}
            text={`${sessionCount} session${pluralize({ itemCount: sessionCount })}`}
          />
        )}
      </VStack>
      <GroupClassSessionHosts hosts={hosts} />
    </VStack>
  </BaseCard>
);

const ConfirmGroupRegistrationBottomSheet = ({
  hosts,
  iconName,
  isOpen,
  isRegistering,
  onClose,
  onConfirm,
  onDismissAnimationEnd,
  recurrenceString,
  sessionCount,
  timeRange,
  title,
}: ConfirmGroupRegistrationBottomSheetProps) => (
  <BottomSheetModal
    contentContainerGap={16}
    dismissAccessibilityLabel="Dismiss Confirm Registration"
    isOpen={isOpen}
    onDismissAnimationEnd={onDismissAnimationEnd}
    onRequestClose={onClose}
    shouldResizeOnContentChange
    testID={baseTestID}
    title={GroupRegistrationText.ConfirmRegistrationTitle}
  >
    <VStack gap={24}>
      <SummaryCard
        hosts={hosts}
        iconName={iconName}
        recurrenceString={recurrenceString}
        sessionCount={sessionCount}
        timeRange={timeRange}
        title={title}
      />
      <Button
        isFullWidth
        isLoading={isRegistering}
        leftIcon={FAIconName.Check}
        onPress={onConfirm}
        testID={joinTestIDs(baseTestID, "confirm")}
        title={GroupRegistrationText.RegisterCta}
      />
    </VStack>
  </BottomSheetModal>
);

export default ConfirmGroupRegistrationBottomSheet;
